import { create } from "zustand";
import axios from "axios";
import { API_BASE_URL1 } from "../../../Services/baseApiUrl";

const fetchData = async (url) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

const getFileTypeFromUrl = (url) => {
  const extension = url.split(".").pop().toLowerCase();
  const mimeTypes = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    pdf: "application/pdf",
    // Add more mime types as needed
  };
  return mimeTypes[extension] || "application/octet-stream"; // Default if not found
};

// Export the store properly with named export
export const useDropdownStore = create((set) => ({
  vehicleTypes: [],
  ownerTypes: [],
  vehicleStatus: [],
  vehicleCapacities: [],
  fuelTypes: [],
  collectionTypes: [],
  zones: [],
  circles: [],
  wards: [],
  drivers: [],
  primaryDeploymentIncharges: [],
  secondaryDeploymentIncharges: [],
  transferStations: [],
  fileInputs: {
    vehicleImageUrl: null,
    rcimageUrl: null,
    pcimageUrl: null,
  },
  filePreviews: {
    vehicleImageUrl: null,
    rcimageUrl: null,
    pcimageUrl: null,
  },
  loading: false,

  // Fetch data and set state
  fetchVehicleTypes: async (collectionTypeId) => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllVehicleTypeByCollectionType?CollectionTypeId=${collectionTypeId}`
    );
    set({ vehicleTypes: data, loading: false });
  },
  fetchVehicleCapacities: async (vehicleTypeId) => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllVehicleCapacityByVehicleType?VehicleTypeId=${vehicleTypeId}`
    );
    set({ vehicleCapacities: data, loading: false });
  },
  fetchFuelTypes: async () => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllFuelType`
    );
    set({ fuelTypes: data, loading: false });
  },
  fetchVehicleStatus: async () => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllVehicleStatus`
    );
    set({ vehicleStatus: data, loading: false });
  },
  fetchCollectionTypes: async () => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllCollectionType`
    );
    set({ collectionTypes: data, loading: false });
  },
  fetchOwnerTypes: async () => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllOwnerTypes`
    );
    set({ ownerTypes: data, loading: false });
  },
  fetchZones: async () => {
    set({ loading: true });
    const data = await fetchData(`${API_BASE_URL1}MasterDetails/GetAllZone`);
    set({ zones: data, loading: false });
  },
  fetchCircles: async (zoneId) => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetCircleByZoneId?ZoneId=${zoneId}`
    );
    set({ circles: data, loading: false });
  },
  fetchWards: async (circleId) => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetWardByCircleId?CircleId=${circleId}`
    );
    set({ wards: data, loading: false });
  },
  fetchDrivers: async (collectionTypeId) => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllDriversByCollectionPointTypeId?collectionPointTypeId=${collectionTypeId}`
    );
    set({ drivers: data, loading: false });
  },

  fetchPrimaryDeploymentIncharges: async () => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}PrimaryDeployment/GetAllPrimaryDeploymentInCharge`
    );
    set({ primaryDeploymentIncharges: data, loading: false });
  },
  fetchSecondaryDeploymentIncharges: async () => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}SecondaryDeployment/GetAllSecondaryDeploymentIncharge`
    );
    set({ secondaryDeploymentIncharges: data, loading: false });
  },
  fetchTransferStations: async (circleId) => {
    set({ loading: true });
    const data = await fetchData(
      `${API_BASE_URL1}MasterDetails/GetAllTransferstation`
    );
    set({ transferStations: data, loading: false });
  },

  // Handle file input change and create preview
  handleFileChange: (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      const fileType = file.type;

      // Set both file and preview
      set((state) => ({
        fileInputs: { ...state.fileInputs, [fieldName]: file },
        filePreviews: {
          ...state.filePreviews,
          [fieldName]: { file, fileType, fileUrl },
        },
      }));
    }
  },

  resetFileInputs: () => {
    set({
      fileInputs: {
        vehicleImageUrl: null,
        rcimageUrl: null,
        pcimageUrl: null,
      },
      filePreviews: {
        vehicleImageUrl: null,
        rcimageUrl: null,
        pcimageUrl: null,
      },
    });
  },
  removeFilePreview: (fieldName) => {
    set((state) => ({
      fileInputs: { ...state.fileInputs, [fieldName]: null },
      filePreviews: { ...state.filePreviews, [fieldName]: null },
    }));
  },
  setFilePreviewFromUrl: (fieldName, url) => {
    if (url) {
      set((state) => ({
        filePreviews: {
          ...state.filePreviews,
          [fieldName]: { fileUrl: url, fileType: getFileTypeFromUrl(url) },
        },
      }));
    }
  },
  updateFilePreview: (vehicleImageUrl, rcimageUrl, pcimageUrl) => {
    set((state) => ({
      filePreviews: {
        ...state.filePreviews,
        vehicleImageUrl: vehicleImageUrl
          ? {
              fileUrl: vehicleImageUrl,
              fileType: getFileTypeFromUrl(vehicleImageUrl),
            }
          : { fileUrl: null, fileType: null }, // Handle null case
        rcimageUrl: rcimageUrl
          ? {
              fileUrl: rcimageUrl,
              fileType: getFileTypeFromUrl(rcimageUrl),
            }
          : { fileUrl: null, fileType: null }, // Handle null case
        pcimageUrl: pcimageUrl
          ? {
              fileUrl: pcimageUrl,
              fileType: getFileTypeFromUrl(pcimageUrl),
            }
          : { fileUrl: null, fileType: null }, // Handle null case
      },
    }));
  },
  clearVehicleTypes: () => set({ vehicleTypes: [] }),
}));



// import { create } from "zustand";
// import axios from "axios";
// import { API_BASE_URL1 } from "../../../Services/baseApiUrl";

// // In-memory cache for fetchData
// const cache = {};
// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// const fetchData = async (url) => {
//   // Check for valid cached response
//   if (cache[url] && Date.now() - cache[url].timestamp < CACHE_DURATION) {
//     // console.log("Returning cached response for:", url);
//     return cache[url].data;
//   }

//   const token = localStorage.getItem("token");
//   try {
//     const response = await axios.get(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     // Store response in cache
//     cache[url] = {
//       data: response.data,
//       timestamp: Date.now(),
//     };

//     return response.data;
//   } catch (error) {
//     console.error("Error fetching data:", error);
//     return [];
//   }
// };

// const getFileTypeFromUrl = (url) => {
//   const extension = url.split(".").pop().toLowerCase();
//   const mimeTypes = {
//     png: "image/png",
//     jpg: "image/jpeg",
//     jpeg: "image/jpeg",
//     gif: "image/gif",
//     pdf: "application/pdf",
//   };
//   return mimeTypes[extension] || "application/octet-stream";
// };

// export const useDropdownStore = create((set) => ({
//   vehicleTypes: [],
//   ownerTypes: [],
//   vehicleStatus: [],
//   vehicleCapacities: [],
//   fuelTypes: [],
//   collectionTypes: [],
//   zones: [],
//   circles: [],
//   wards: [],
//   drivers: [],
//   primaryDeploymentIncharges: [],
//   secondaryDeploymentIncharges: [],
//   transferStations: [],
//   fileInputs: {
//     vehicleImageUrl: null,
//     rcimageUrl: null,
//     pcimageUrl: null,
//   },
//   filePreviews: {
//     vehicleImageUrl: null,
//     rcimageUrl: null,
//     pcimageUrl: null,
//   },
//   loading: false,

//   fetchVehicleTypes: async (collectionTypeId) => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllVehicleTypeByCollectionType?CollectionTypeId=${collectionTypeId}`
//     );
//     set({ vehicleTypes: data, loading: false });
//   },
//   fetchVehicleCapacities: async (vehicleTypeId) => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllVehicleCapacityByVehicleType?VehicleTypeId=${vehicleTypeId}`
//     );
//     set({ vehicleCapacities: data, loading: false });
//   },
//   fetchFuelTypes: async () => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllFuelType`
//     );
//     set({ fuelTypes: data, loading: false });
//   },
//   fetchVehicleStatus: async () => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllVehicleStatus`
//     );
//     set({ vehicleStatus: data, loading: false });
//   },
//   fetchCollectionTypes: async () => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllCollectionType`
//     );
//     set({ collectionTypes: data, loading: false });
//   },
//   fetchOwnerTypes: async () => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllOwnerTypes`
//     );
//     set({ ownerTypes: data, loading: false });
//   },
//   fetchZones: async () => {
//     set({ loading: true });
//     const data = await fetchData(`${API_BASE_URL1}MasterDetails/GetAllZone`);
//     set({ zones: data, loading: false });
//   },
//   fetchCircles: async (zoneId) => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetCircleByZoneId?ZoneId=${zoneId}`
//     );
//     set({ circles: data, loading: false });
//   },
//   fetchWards: async (circleId) => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetWardByCircleId?CircleId=${circleId}`
//     );
//     set({ wards: data, loading: false });
//   },
//   fetchDrivers: async (collectionTypeId) => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllDriversByCollectionPointTypeId?collectionPointTypeId=${collectionTypeId}`
//     );
//     set({ drivers: data, loading: false });
//   },
//   fetchPrimaryDeploymentIncharges: async () => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}PrimaryDeployment/GetAllPrimaryDeploymentInCharge`
//     );
//     set({ primaryDeploymentIncharges: data, loading: false });
//   },
//   fetchSecondaryDeploymentIncharges: async () => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}SecondaryDeployment/GetAllSecondaryDeploymentIncharge`
//     );
//     set({ secondaryDeploymentIncharges: data, loading: false });
//   },
//   fetchTransferStations: async (circleId) => {
//     set({ loading: true });
//     const data = await fetchData(
//       `${API_BASE_URL1}MasterDetails/GetAllTransferstation`
//     );
//     set({ transferStations: data, loading: false });
//   },

//   handleFileChange: (e, fieldName) => {
//     const file = e.target.files[0];
//     if (file) {
//       const fileUrl = URL.createObjectURL(file);
//       const fileType = file.type;
//       set((state) => ({
//         fileInputs: { ...state.fileInputs, [fieldName]: file },
//         filePreviews: {
//           ...state.filePreviews,
//           [fieldName]: { file, fileType, fileUrl },
//         },
//       }));
//     }
//   },

//   resetFileInputs: () => {
//     set({
//       fileInputs: {
//         vehicleImageUrl: null,
//         rcimageUrl: null,
//         pcimageUrl: null,
//       },
//       filePreviews: {
//         vehicleImageUrl: null,
//         rcimageUrl: null,
//         pcimageUrl: null,
//       },
//     });
//   },

//   updateFilePreview: (vehicleImageUrl, rcimageUrl, pcimageUrl) => {
//     set((state) => ({
//       filePreviews: {
//         ...state.filePreviews,
//         vehicleImageUrl: vehicleImageUrl
//           ? {
//               fileUrl: vehicleImageUrl,
//               fileType: getFileTypeFromUrl(vehicleImageUrl),
//             }
//           : { fileUrl: null, fileType: null },
//         rcimageUrl: rcimageUrl
//           ? {
//               fileUrl: rcimageUrl,
//               fileType: getFileTypeFromUrl(rcimageUrl),
//             }
//           : { fileUrl: null, fileType: null },
//         pcimageUrl: pcimageUrl
//           ? {
//               fileUrl: pcimageUrl,
//               fileType: getFileTypeFromUrl(pcimageUrl),
//             }
//           : { fileUrl: null, fileType: null },
//       },
//     }));
//   },

//   clearVehicleTypes: () => set({ vehicleTypes: [] }),
// }));